import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { addTournament } from "../../../../../store/tournament/actions/add";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Link,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  Wrap,
} from "@chakra-ui/react";
import { toggleTournamentVisibility } from "../../../../../store/tournament/actions/toggleVisibility";
import { DeleteIcon } from "@chakra-ui/icons";
import { deleteTournament } from "../../../../../store/tournament/actions/delete";
import { TalesSpinner } from "../../../../atoms";
import { FileUploader } from "../../../../atoms/FileUploader";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "../../../../../store";
import {
  getTournamentData,
  getTournamentError,
  isTournamentLoading,
} from "../../../../../store/tournament/selectors";
import useTournaments from "../../../../../utils/hooks/useTournaments";

interface TournamentInput {
  title: string;
  show: boolean;
  description?: string;
  area: string;
  data: string;
  ora: string;
  website?: string;
}
export default function Tournaments() {
  useTournaments();
  const dispatch = useDispatch<AppThunkDispatch>();
  const isPending = useSelector(isTournamentLoading);
  const tournaments = useSelector(getTournamentData);
  const error = useSelector(getTournamentError);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = useForm<TournamentInput>({
    defaultValues: {
      title: "",
      description: "",
      area: "",
      data: "",
      ora: "",
      show: true,
    },
  });
  const [file, setFile] = useState<File>();

  const onSubmit: SubmitHandler<TournamentInput> = (data) => {
    const newTournament = {
      title: getValues("title"),
      description: getValues("description"),
      website: getValues("website"),
      data: getValues("data"),
      ora: getValues("ora"),
      area: getValues("area"),
      show: getValues("show"),
      file: file,
    };
    dispatch(addTournament(newTournament));
  };

  return (
    <>
      {error && (
        <Box>
          <Text my="10px">{error}</Text>
        </Box>
      )}
      <Wrap>
        {tournaments?.map((tournament) => (
          <Card key={tournament.logoUrl} w="md" m="10px">
            <CardHeader>
              <Heading size="base">{tournament.title}</Heading>
              <Heading size="base" fontWeight="normal">
                {tournament.area}
              </Heading>
              <Heading size="base" fontWeight="normal">
                {tournament.data.split("-").reverse().join("/")}, h{" "}
                {tournament.ora}
              </Heading>
            </CardHeader>
            <CardBody>
              <Text whiteSpace="pre-line">{tournament.description}</Text>
              {tournament.website && (
                <Link href={tournament.website}>{tournament.website}</Link>
              )}
              <Image
                src={tournament.logoUrl}
                maxH="80px"
                maxW="100%"
                objectFit="contain"
                alt={tournament.title}
              />
            </CardBody>
            <CardFooter>
              <Button
                aria-label={tournament.show ? "Nascondi" : "Mostra"}
                variant="solid"
                colorScheme={tournament.show ? "gray" : "green"}
                onClick={() => dispatch(toggleTournamentVisibility(tournament))}
                marginRight="20px"
              >
                {tournament.show ? "NASCONDI DAL SITO" : "MOSTRA SUL SITO"}
              </Button>
              <IconButton
                aria-label="Elimina"
                variant="solid"
                icon={<DeleteIcon />}
                colorScheme="red"
                onClick={() => dispatch(deleteTournament(tournament))}
              />
            </CardFooter>
          </Card>
        ))}
        <Card w="lg" m="10px">
          {isPending && (
            <CardBody>
              <Box w="30px" m="auto">
                <TalesSpinner />
              </Box>
            </CardBody>
          )}
          {!isPending && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader>
                <Heading size="base">Aggiungi nuovo</Heading>
              </CardHeader>
              <CardBody>
                <FormControl
                  marginBottom="20px"
                  isInvalid={errors.title != null}
                  isRequired={true}
                >
                  <FormLabel htmlFor="title">Titolo</FormLabel>
                  <Input
                    id="title"
                    type="text"
                    {...register("title", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  marginBottom="20px"
                  isInvalid={errors.area != null}
                  isRequired={true}
                >
                  <FormLabel htmlFor="area">Dove?</FormLabel>
                  <Input
                    id="area"
                    type="area"
                    {...register("area", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.area && errors.area.message}
                  </FormErrorMessage>
                </FormControl>

                <HStack marginBottom="20px" spacing="10px">
                  <FormControl
                    isInvalid={errors.data != null}
                    isRequired={true}
                  >
                    <FormLabel htmlFor="data">Data</FormLabel>
                    <Input
                      id="data"
                      type="date"
                      {...register("data", { required: "Campo richiesto" })}
                    />
                    <FormErrorMessage>
                      {errors.data && errors.data.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={errors.ora != null} isRequired={true}>
                    <FormLabel htmlFor="title">Ora</FormLabel>
                    <Input
                      id="ora"
                      type="text"
                      {...register("ora", { required: "Campo richiesto" })}
                    />
                    <FormErrorMessage>
                      {errors.ora && errors.ora.message}
                    </FormErrorMessage>
                  </FormControl>
                </HStack>

                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="description">Descrizione</FormLabel>
                  <Textarea
                    whiteSpace="pre-wrap"
                    id="description"
                    {...register("description")}
                  />
                  <FormErrorMessage>
                    {errors.description && errors.description.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="website">Sito</FormLabel>
                  <Input id="website" type="text" {...register("website")} />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px">
                  <FormLabel>Immagine</FormLabel>
                  <FileUploader handleFile={(file) => setFile(file)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Mostrare sul sito</FormLabel>
                  <RadioGroup defaultValue={getValues("show").toString()}>
                    <Flex>
                      <Radio
                        value="true"
                        marginRight="30px"
                        {...register("show")}
                      >
                        Sì
                      </Radio>
                      <Radio value="false" {...register("show")}>
                        No
                      </Radio>
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </CardBody>
              <CardFooter>
                <Button
                  type="submit"
                  isDisabled={
                    isPending ||
                    watch("title") === "" ||
                    watch("data") === "" ||
                    watch("ora") === "" ||
                    watch("area") === "" ||
                    !watch("title") ||
                    !watch("data") ||
                    !watch("ora") ||
                    !watch("area") ||
                    tournaments?.some(
                      (tournament) =>
                        tournament.title === watch("title") &&
                        tournament.data === watch("data") &&
                        tournament.area === watch("area") &&
                        tournament.ora === watch("ora"),
                    )
                  }
                  colorScheme="green"
                >
                  Crea
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </Wrap>
    </>
  );
}
